export default class ProductHero {
    constructor(component) {
        this.gallery = null;
        this.dots = null;
        this.defaultGalleryHtml = '';
        this.variations = [];
        this.specs = null;
        this.defaultSpecsHtml = '';
        this.variationSpecs = [];
        this.component = component;
        this.init();
    }
    init() {
        var _a;
        const self = this;
        // Gallery swapping setup — cache the default markup + parse the
        // pre-rendered per-variation galleries printed by the template.
        // The server may render a variation as the initial gallery (to match the
        // ?color=&size= URL params), so the true default is published separately
        // in [data-default-gallery]; fall back to the current markup when absent.
        self.gallery = self.component.querySelector('[data-product-gallery]');
        if (self.gallery) {
            self.defaultGalleryHtml = (_a = self.parseDefaultGallery()) !== null && _a !== void 0 ? _a : self.gallery.innerHTML;
        }
        self.variations = self.parseVariations();
        // Mobile slider dots. Below lg the gallery is a scroll-snap strip (CSS);
        // the dots show where you are and jump to a slide. They sit outside the
        // gallery because its markup is replaced on every swap.
        self.dots = self.component.querySelector('[data-gallery-dots]');
        if (self.gallery && self.dots) {
            let frame = 0;
            self.gallery.addEventListener('scroll', () => {
                cancelAnimationFrame(frame);
                frame = requestAnimationFrame(() => self.markActiveDot());
            }, { passive: true });
            self.buildDots();
        }
        // Technical specs. Weight and dimensions belong to the variation, not
        // the product, so the table has to follow the selection the same way the
        // gallery does. The server renders the table for the initial selection,
        // so the parent-level fallback is published separately.
        self.specs = self.component.querySelector('[data-product-specs]');
        self.variationSpecs = self.parseIsland('[data-variation-specs]', []);
        self.defaultSpecsHtml = self.parseIsland('[data-default-specs]', '')
            || (self.specs ? self.specs.innerHTML : '');
        // Option tags — single active selection per group (size, color).
        self.component.querySelectorAll('[data-option-group]').forEach((group) => {
            const tags = group.querySelectorAll('[data-option]');
            tags.forEach((tag) => {
                tag.addEventListener('click', () => {
                    // A second click on the active option clears the selection,
                    // returning the gallery (and URL) to the product's default view.
                    // aria-pressed mirrors is-active so screen readers hear the
                    // selected size / colour (client audit 25.09, UP-12).
                    const wasActive = tag.classList.contains('is-active');
                    tags.forEach((t) => {
                        t.classList.remove('is-active');
                        t.setAttribute('aria-pressed', 'false');
                    });
                    if (!wasActive) {
                        tag.classList.add('is-active');
                        tag.setAttribute('aria-pressed', 'true');
                    }
                    self.swapGallery();
                    self.swapSpecs();
                    self.updateUrl();
                });
            });
        });
        // FAQ accordion — one panel open at a time.
        const items = self.component.querySelectorAll('[data-faq-item]');
        items.forEach((item) => {
            const toggle = item.querySelector('[data-faq-toggle]');
            const panel = item.querySelector('[data-faq-panel]');
            if (!toggle || !panel) {
                return;
            }
            toggle.addEventListener('click', () => {
                const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                items.forEach((other) => {
                    var _a, _b;
                    (_a = other.querySelector('[data-faq-toggle]')) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'false');
                    (_b = other.querySelector('[data-faq-panel]')) === null || _b === void 0 ? void 0 : _b.setAttribute('hidden', '');
                });
                if (!isOpen) {
                    toggle.setAttribute('aria-expanded', 'true');
                    panel.removeAttribute('hidden');
                }
            });
        });
    }
    // The true default gallery markup, when the server rendered a variation initially.
    parseDefaultGallery() {
        const island = this.component.querySelector('[data-default-gallery]');
        if (!island || !island.textContent) {
            return null;
        }
        try {
            const html = JSON.parse(island.textContent);
            return typeof html === 'string' ? html : null;
        }
        catch (_a) {
            return null;
        }
    }
    // JSON printed by the template into a <script type="application/json">.
    parseIsland(selector, fallback) {
        const island = this.component.querySelector(selector);
        if (!island || !island.textContent) {
            return fallback;
        }
        try {
            return JSON.parse(island.textContent);
        }
        catch (_a) {
            return fallback;
        }
    }
    parseVariations() {
        const island = this.component.querySelector('[data-variation-galleries]');
        if (!island || !island.textContent) {
            return [];
        }
        try {
            const data = JSON.parse(island.textContent);
            return Array.isArray(data) ? data : [];
        }
        catch (_a) {
            return [];
        }
    }
    // Active slug for an option group, or '' when nothing is selected.
    activeOption(groupName) {
        var _a;
        const active = this.component.querySelector(`[data-option-group="${groupName}"] .is-active[data-option]`);
        return (_a = active === null || active === void 0 ? void 0 : active.getAttribute('data-option')) !== null && _a !== void 0 ? _a : '';
    }
    // Best match for the current color/size, by priority:
    // color+size → color-only → size-only. Returns null to fall back to default.
    matchVariation(list, color, size) {
        const find = (c, s) => { var _a; return (_a = list.find((v) => v.color === c && v.size === s)) !== null && _a !== void 0 ? _a : null; };
        if (color && size) {
            const exact = find(color, size);
            if (exact) {
                return exact;
            }
        }
        if (color) {
            const colorOnly = find(color, '');
            if (colorOnly) {
                return colorOnly;
            }
        }
        if (size) {
            const sizeOnly = find('', size);
            if (sizeOnly) {
                return sizeOnly;
            }
        }
        return null;
    }
    // Reflect the active color/size in the address bar so the current view is
    // shareable. replaceState keeps history clean across rapid swatch clicks.
    updateUrl() {
        const url = new URL(window.location.href);
        const color = this.activeOption('color');
        const size = this.activeOption('size');
        if (color) {
            url.searchParams.set('color', color);
        }
        else {
            url.searchParams.delete('color');
        }
        if (size) {
            url.searchParams.set('size', size);
        }
        else {
            url.searchParams.delete('size');
        }
        window.history.replaceState(null, '', url);
    }
    swapGallery() {
        if (!this.gallery || this.variations.length === 0) {
            return;
        }
        const match = this.matchVariation(this.variations, this.activeOption('color'), this.activeOption('size'));
        const html = match ? match.html : this.defaultGalleryHtml;
        // No-op if the markup is already showing.
        if (this.gallery.innerHTML.trim() === html.trim()) {
            return;
        }
        const gallery = this.gallery;
        gallery.classList.add('is-swapping');
        window.setTimeout(() => {
            gallery.innerHTML = html;
            // A new set starts at its first picture — that is the one the pick changed.
            gallery.scrollLeft = 0;
            this.buildDots();
            gallery.classList.remove('is-swapping');
        }, 150);
    }
    slides() {
        return this.gallery
            ? Array.from(this.gallery.querySelectorAll('.product-gallery__media'))
            : [];
    }
    // One dot per slide; hidden when there is nothing to swipe to.
    buildDots() {
        if (!this.dots || !this.gallery) {
            return;
        }
        const slides = this.slides();
        const gallery = this.gallery;
        this.dots.innerHTML = '';
        this.dots.hidden = slides.length < 2;
        slides.forEach((slide, i) => {
            var _a;
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.setAttribute('aria-label', `${i + 1} / ${slides.length}`);
            dot.addEventListener('click', () => {
                gallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
            });
            (_a = this.dots) === null || _a === void 0 ? void 0 : _a.appendChild(dot);
        });
        this.markActiveDot();
    }
    markActiveDot() {
        if (!this.dots || !this.gallery) {
            return;
        }
        const slides = this.slides();
        const step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 0;
        const index = step > 0 ? Math.round(this.gallery.scrollLeft / step) : 0;
        Array.from(this.dots.children).forEach((dot, i) => {
            dot.classList.toggle('is-active', i === index);
            if (i === index) {
                dot.setAttribute('aria-current', 'true');
            }
            else {
                dot.removeAttribute('aria-current');
            }
        });
    }
    // No fade here: the gallery is a picture and benefits from the transition,
    // whereas a table of numbers reads as broken if it blanks before changing.
    swapSpecs() {
        if (!this.specs || this.variationSpecs.length === 0) {
            return;
        }
        const match = this.matchVariation(this.variationSpecs, this.activeOption('color'), this.activeOption('size'));
        const html = match ? match.html : this.defaultSpecsHtml;
        if (this.specs.innerHTML.trim() === html.trim()) {
            return;
        }
        this.specs.innerHTML = html;
    }
}
